import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import useWizardStore from '@zs/useWizardStore';
import * as common from '@/common';
import * as elements from '@svg/elements';
import {
  octSettings,
  octBoundsDeg,
} from '@/imageFiles';
import { useImageCache } from '@/useImageCache';
import { useOctViewer } from '@svg/useOctViewer';
import { useEnfaceViewer } from '@svg/useEnfaceViewer';
import ZoomPan from '@svg/ZoomPan';
import BScroller from '@svg/BScroller';


interface ImageBrowserProps {
  eye: 'OD' | 'OS';
  alignRED: 'L' | 'R';
  alignScroll: 'L' | 'R';
}
export const ImageBrowser = ({
  eye,
  alignRED,
  alignScroll,
}: ImageBrowserProps): JSX.Element => {
  const cacheHook = useImageCache(eye);

  const [redMatrix, setRedMatrix] = useState<DOMMatrix>(new DOMMatrix());
  const [fafMatrix, setFafMatrix] = useState<DOMMatrix>(new DOMMatrix());

  const {
    slice: { bSlice, setBSlice, handleBSlice },
    index: { bIndex, setBIndex, handleBScroll },
    enface: scanCoord,
  } = useOctViewer(eye);
  const enfaceRED = useEnfaceViewer(eye, true);
  const enfaceFAF = useEnfaceViewer(eye, false);

  const handleRedZoomPan = useCallback((m: DOMMatrix) => {
    setRedMatrix(m);
  }, [setRedMatrix]);

  const handleFafZoomPan = useCallback((m: DOMMatrix) => {
    setFafMatrix(m);
  }, [setFafMatrix]);



  if (!cacheHook) {
    return <div className='flex flex-col items-center gap-2'>
      <div>Loading images...</div>
      <div className='dotpulse-container'>
        <div className='dotpulse' />
      </div>
    </div>;
  }

  const {
    BSCAN,
    RED,
    FAF,
  } = cacheHook;

  const enfaceFlexRow = alignRED == 'L' ? 'flex-row' : 'flex-row-reverse';
  const scrollFlexRow = alignScroll == 'L' ? 'flex-row' : 'flex-row-reverse';

  const foveaOverlay = !enfaceRED.fovDeg
    ? undefined
    : elements.renderFoveaCircle(enfaceRED.fovDeg, 'red');

  const enfaceLines: JSX.Element = <>
    <line x1={scanCoord.x} y1={0} x2={scanCoord.x} y2={fullDeg} stroke='#FF0' strokeWidth={0.1} opacity={0.5} />
    <line x1={octBoundsDeg.xMin} y1={scanCoord.y} x2={octBoundsDeg.xMax} y2={scanCoord.y} stroke='#0F0' strokeWidth={0.1} opacity={0.5} />
  </>;

  const infra = <svg viewBox={`0 0 ${fullDeg} ${fullDeg}`} className='w-72 h-72'>
    <ZoomPan minZoom={1} maxZoom={4} matrix={redMatrix} onMatrixChange={handleRedZoomPan}>
      <rect fill='black' width={4*fullDeg} height={4*fullDeg} x={-2*fullDeg} y={-2*fullDeg} />
      <g transform={`scale(${enfaceRED.scale})`}>
        <image crossOrigin='anonymous' href={RED.url} />
      </g>
      {foveaOverlay}
      {enfaceLines}
    </ZoomPan>
  </svg>;

  const fluoro = <svg viewBox={`0 0 ${fullDeg} ${fullDeg}`} className='w-72 h-72'>
    <ZoomPan minZoom={1} maxZoom={4} matrix={fafMatrix} onMatrixChange={handleFafZoomPan}>
      <rect fill='black' width={4*fullDeg} height={4*fullDeg} x={-2*fullDeg} y={-2*fullDeg} />
      <rect fill='gray' width={fullDeg} height={fullDeg} />
      <g transform={`scale(${enfaceFAF.scale})`}>
        <g transform={enfaceFAF.regAttr}>
          <image crossOrigin='anonymous' href={FAF.url} />
        </g>
      </g>
      {!!enfaceFAF.regAttr && scanBox}
      {!!enfaceFAF.regAttr && foveaOverlay}
      {!!enfaceFAF.regAttr && enfaceLines}
    </ZoomPan>
  </svg>;


  return <div className='flex flex-col gap-2'>
    <div className={`flex ${enfaceFlexRow} justify-between gap-2`}>
      <div>
        {infra}
        <div className='text-sm'>{eye} Infrared (reference)</div>
      </div>
      <div>
        {fluoro}
        <div className='text-sm'>{eye} Autofluorescence ({!!enfaceFAF.regAttr ? '' : 'NOT '}registered)</div>
      </div>
    </div>
    <div className={`flex ${scrollFlexRow} gap-1`}>
      <div className='flex flex-col gap-1'>
        <div className='text-xs'>Scan No.</div>
        <input
          type='number'
          value={bIndex + 1}
          onChange={handleBScroll}
          step={1}
          min={1}
          max={BSCAN.urls.length}
          className='w-12 border rounded-md'
        />
        <input
          type='range'
          value={bIndex + 1}
          onChange={handleBScroll}
          step={1}
          min={1}
          max={BSCAN.urls.length}
          style={{ writingMode: 'vertical-rl', direction: 'rtl' }}
          className='grow'
        />
      </div>
      <svg
        className='w-[600px]'
        viewBox={`${BSCAN.crop.x} ${BSCAN.crop.y} ${BSCAN.crop.width} ${BSCAN.crop.height}`}
      >
        <BScroller eye={eye} index={bIndex} onSlice={handleBSlice} />
      </svg>
    </div>
  </div>
}


const fullDeg = octSettings.enfaceAngle;
const scanBox = elements.renderScanBox();
